/**
 * Interactive SVG Yard Map Canvas.
 *
 * Renders yard sections as SVG shapes with support for drag-to-move,
 * resize handles, snap alignment guides, distance-to-boundary indicators,
 * dimension rulers, and multiple shape types (rectangle, circle, L-shape,
 * T-shape, triangle, custom polygon).
 *
 * This component owns all canvas interaction state and delegates position/size
 * persistence back to the parent via callbacks.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { Minus, Plus, Maximize2, LocateFixed, Map } from "lucide-react";
import SectionShape from "./SectionShape";
import FeatureShape from "./FeatureShape";
import MapMinimap from "./MapMinimap";
import ResizeHandles from "./ResizeHandles";
import DimensionRulers from "./DimensionRulers";
import AlignmentGuides from "./AlignmentGuides";
import { useCanvasDrag } from "./useCanvasDrag";
import { useCanvasResize } from "./useCanvasResize";
import { useBoundaryDraw } from "./useBoundaryDraw";
import {
  formatLength,
  pointsToSvgString,
  segmentLength,
  orthoConstrain,
  unitVector,
  type ShapeType,
  type Point,
} from "./shapeUtils";
import type { LocationScheme } from "@/types/locations";
import type { YardFeature } from "@/types/yard";

/** MIME type used when dragging an unplaced storage area onto the canvas. */
export const AREA_DRAG_MIME = "application/x-traderoute-area";

/** Render midpoint length labels for a chain of points (open or closed). */
function SegmentLengths({
  points,
  closed,
  unit,
  scale,
}: {
  points: Point[];
  closed: boolean;
  unit: string;
  scale: number;
}) {
  const edges: Array<[Point, Point]> = [];
  for (let i = 0; i < points.length - 1; i++) {
    edges.push([points[i]!, points[i + 1]!]);
  }
  if (closed && points.length >= 3) {
    edges.push([points[points.length - 1]!, points[0]!]);
  }
  return (
    <g style={{ pointerEvents: "none" }}>
      {edges.map(([a, b], i) => {
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        return (
          <text
            key={i}
            x={mx}
            y={my}
            fontSize={9 / scale}
            fill="var(--text-secondary, #475569)"
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ paintOrder: "stroke" }}
            stroke="var(--color-surface-secondary, #f8fafc)"
            strokeWidth={3 / scale}
            strokeLinejoin="round"
          >
            {formatLength(segmentLength(a, b), unit)}
          </text>
        );
      })}
    </g>
  );
}

interface CanvasSection {
  id: string;
  label: string;
  section_type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  capacity: number | null;
  asset_count: number;
  shape_type?: string | null;
  points?: Array<{ x: number; y: number }> | null;
  rotation?: number | null;
  location_scheme_id?: string | null;
  location_count?: number;
  assets?: Array<{ id: string; sku: string; name: string; status: string }>;
}

interface YardCanvasProps {
  yardId: string;
  yardWidth: number;
  yardHeight: number;
  /** Unit of measure for dimensions and segment-length readouts. */
  unit: string;
  /** Custom boundary polygon; null renders the default rectangular boundary. */
  boundaryPoints?: Point[] | null;
  /** Dashed setback (keep-clear) polygon inset from the boundary. */
  setbackPoints?: Point[] | null;
  sections: CanvasSection[];
  selectedSectionId: string | null;
  onSelectSection: (id: string | null) => void;
  /** Reference features (buildings, fences, ...) to render as a layer. */
  features?: YardFeature[];
  selectedFeatureId?: string | null;
  onSelectFeature?: (id: string | null) => void;
  schemes: LocationScheme[];
  /** Base fit-to-container scale; the canvas multiplies this by user zoom. */
  fitScale: number;
  /** When true, clicks place vertices instead of selecting sections. */
  drawMode?: boolean;
  /** Geometry the pen tool is tracing (open polyline vs closed polygon). */
  drawGeometry?: "polygon" | "polyline";
  /** Fired with the finished vertex list when a shape is completed. */
  onBoundaryComplete?: (points: Point[]) => void;
  /** Fired to abort an in-progress draw (Escape). */
  onCancelDraw?: () => void;
  /** Fired when an unplaced storage area is dropped onto the canvas. */
  onDropArea?: (areaId: string, x: number, y: number) => void;
  /**
   * When provided, drag/resize commit geometry through the parent (which
   * persists + records undo history) instead of the canvas's own mutation.
   */
  onCommitGeometry?: (
    sectionId: string,
    before: Record<string, number>,
    after: Record<string, number>,
  ) => void;
  /** Section/feature ids that are locked (drag/resize disabled). */
  lockedIds?: Set<string>;
  /** Right-click on a shape → open a context menu at the cursor. */
  onShapeContextMenu?: (target: {
    type: "section" | "feature";
    id: string;
    x: number;
    y: number;
  }) => void;
}

/** Muted palette for scheme badges. */
const SCHEME_BADGE_COLORS = [
  { bg: "#e0e7ff", text: "#4338ca" }, // indigo
  { bg: "#ccfbf1", text: "#0f766e" }, // teal
  { bg: "#ffe4e6", text: "#be123c" }, // rose
  { bg: "#fef3c7", text: "#92400e" }, // amber
  { bg: "#ede9fe", text: "#6d28d9" }, // violet
  { bg: "#cffafe", text: "#0e7490" }, // cyan
];

function getSchemeBadgeColor(schemeId: string, schemes: LocationScheme[]) {
  const idx = schemes.findIndex((s) => s.id === schemeId);
  return SCHEME_BADGE_COLORS[
    (idx >= 0 ? idx : 0) % SCHEME_BADGE_COLORS.length
  ]!;
}

function getSchemeName(schemeId: string, schemes: LocationScheme[]): string {
  return schemes.find((s) => s.id === schemeId)?.name ?? "Scheme";
}

function YardCanvas({
  yardId,
  yardWidth,
  yardHeight,
  unit,
  boundaryPoints,
  setbackPoints,
  sections,
  selectedSectionId,
  onSelectSection,
  features = [],
  selectedFeatureId = null,
  onSelectFeature,
  schemes,
  fitScale,
  drawMode = false,
  drawGeometry = "polygon",
  onBoundaryComplete,
  onCancelDraw,
  onDropArea,
  onCommitGeometry,
  lockedIds,
  onShapeContextMenu,
}: YardCanvasProps) {
  const qc = useQueryClient();
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [lengthInput, setLengthInput] = useState("");

  // --- Viewport: user zoom on top of the fit scale, pan via wrapper scroll ---
  const MIN_ZOOM = 0.2;
  const MAX_ZOOM = 8;
  const [zoom, setZoom] = useState(1);
  const scale = Math.max(0.02, Math.min(40, fitScale * zoom));

  // Screen-pixel position of a map coordinate inside the SVG. The ruler gutter
  // contributes a constant +20px regardless of scale, so pixel = coord*scale+20.
  const RULER_PX = 20;

  // Viewport rect (for the minimap): scroll position + client size in px.
  const [view, setView] = useState({ left: 0, top: 0, cw: 0, ch: 0 });
  const syncView = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    setView({
      left: el.scrollLeft,
      top: el.scrollTop,
      cw: el.clientWidth,
      ch: el.clientHeight,
    });
  }, []);
  useEffect(() => {
    syncView();
  }, [syncView, scale]);

  // Pending focal correction: after a zoom re-renders the SVG at a new size,
  // scroll so the focal map point stays under the same on-screen point.
  const pendingFocus = useRef<{
    mx: number;
    my: number;
    cx: number;
    cy: number;
  } | null>(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    const f = pendingFocus.current;
    if (!el || !f) return;
    el.scrollLeft = f.mx * scale + RULER_PX - f.cx;
    el.scrollTop = f.my * scale + RULER_PX - f.cy;
    pendingFocus.current = null;
    syncView();
  }, [scale, syncView]);

  /** Apply a new zoom keeping the given wrapper-relative point (cx,cy) fixed. */
  const applyZoom = useCallback(
    (nextZoom: number, cx: number, cy: number) => {
      const el = wrapRef.current;
      if (!el) return;
      const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nextZoom));
      const mx = (el.scrollLeft + cx - RULER_PX) / scale;
      const my = (el.scrollTop + cy - RULER_PX) / scale;
      pendingFocus.current = { mx, my, cx, cy };
      setZoom(clamped);
    },
    [scale],
  );

  const zoomBy = useCallback(
    (factor: number) => {
      const el = wrapRef.current;
      if (!el) return;
      applyZoom(zoom * factor, el.clientWidth / 2, el.clientHeight / 2);
    },
    [zoom, applyZoom],
  );

  const resetZoom = useCallback(() => {
    pendingFocus.current = null;
    setZoom(1);
    const el = wrapRef.current;
    if (el) {
      el.scrollLeft = 0;
      el.scrollTop = 0;
    }
  }, []);

  /** Fit a map-unit bounding box into the viewport and center it. */
  const zoomToBox = useCallback(
    (minX: number, minY: number, maxX: number, maxY: number) => {
      const el = wrapRef.current;
      if (!el) return;
      const pad = 40; // px of breathing room
      const bw = Math.max(1, maxX - minX);
      const bh = Math.max(1, maxY - minY);
      const targetScale = Math.min(
        (el.clientWidth - pad) / bw,
        (el.clientHeight - pad) / bh,
      );
      const nextZoom = Math.max(
        MIN_ZOOM,
        Math.min(MAX_ZOOM, targetScale / fitScale),
      );
      const nextScale = Math.max(0.02, Math.min(40, fitScale * nextZoom));
      const cxMap = (minX + maxX) / 2;
      const cyMap = (minY + maxY) / 2;
      pendingFocus.current = {
        mx: cxMap,
        my: cyMap,
        cx: el.clientWidth / 2,
        cy: el.clientHeight / 2,
      };
      // If zoom won't actually change, apply the scroll correction directly.
      if (Math.abs(nextZoom - zoom) < 1e-4) {
        el.scrollLeft = cxMap * nextScale + RULER_PX - el.clientWidth / 2;
        el.scrollTop = cyMap * nextScale + RULER_PX - el.clientHeight / 2;
        pendingFocus.current = null;
        syncView();
      } else {
        setZoom(nextZoom);
      }
    },
    [fitScale, zoom, syncView],
  );

  /** Zoom to the currently selected shape, if any. */
  const zoomToSelection = useCallback(() => {
    const sel =
      sections.find((s) => s.id === selectedSectionId) ??
      (selectedFeatureId
        ? features.find((f) => f.id === selectedFeatureId)
        : undefined);
    if (!sel) return;
    if ("points" in sel && sel.points && sel.points.length >= 2) {
      const xs = sel.points.map((p) => p.x);
      const ys = sel.points.map((p) => p.y);
      zoomToBox(
        Math.min(...xs),
        Math.min(...ys),
        Math.max(...xs),
        Math.max(...ys),
      );
    } else if ("x" in sel) {
      zoomToBox(sel.x, sel.y, sel.x + sel.width, sel.y + sel.height);
    }
  }, [sections, features, selectedSectionId, selectedFeatureId, zoomToBox]);

  const hasSelection = !!selectedSectionId || !!selectedFeatureId;

  // Ctrl/Cmd + wheel zooms at the cursor. Attached natively (non-passive) so
  // preventDefault reliably suppresses the browser's page zoom. Latest zoom /
  // applyZoom are read through refs so the listener is attached only once.
  const applyZoomRef = useRef(applyZoom);
  applyZoomRef.current = applyZoom;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const rect = el.getBoundingClientRect();
        applyZoomRef.current(
          zoomRef.current * (e.deltaY < 0 ? 1.12 : 1 / 1.12),
          e.clientX - rect.left,
          e.clientY - rect.top,
        );
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Space-to-pan: hold Space (or middle-drag) and drag to pan the viewport.
  const [spaceHeld, setSpaceHeld] = useState(false);
  const spaceRef = useRef(false);
  const panRef = useRef<{
    sx: number;
    sy: number;
    sl: number;
    st: number;
  } | null>(null);
  const [grabbing, setGrabbing] = useState(false);

  useEffect(() => {
    const isField = (t: EventTarget | null) => {
      const tag = (t as HTMLElement | null)?.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    };
    const down = (e: KeyboardEvent) => {
      if (e.code === "Space" && !isField(e.target) && !drawMode) {
        spaceRef.current = true;
        setSpaceHeld(true);
        e.preventDefault();
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        spaceRef.current = false;
        setSpaceHeld(false);
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [drawMode]);

  const handleWrapPointerDown = useCallback((e: React.PointerEvent) => {
    if (!(spaceRef.current || e.button === 1)) return;
    const el = wrapRef.current;
    if (!el) return;
    e.preventDefault();
    panRef.current = {
      sx: e.clientX,
      sy: e.clientY,
      sl: el.scrollLeft,
      st: el.scrollTop,
    };
    setGrabbing(true);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }, []);
  const handleWrapPointerMove = useCallback((e: React.PointerEvent) => {
    const p = panRef.current;
    const el = wrapRef.current;
    if (!p || !el) return;
    el.scrollLeft = p.sl - (e.clientX - p.sx);
    el.scrollTop = p.st - (e.clientY - p.sy);
  }, []);
  const handleWrapPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!panRef.current) return;
      panRef.current = null;
      setGrabbing(false);
      syncView();
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    },
    [syncView],
  );

  const [showMinimap, setShowMinimap] = useState(true);

  /** Center the viewport on a map coordinate (used by the minimap). */
  const centerOn = useCallback(
    (mx: number, my: number) => {
      const el = wrapRef.current;
      if (!el) return;
      el.scrollLeft = mx * scale + RULER_PX - el.clientWidth / 2;
      el.scrollTop = my * scale + RULER_PX - el.clientHeight / 2;
      syncView();
    },
    [scale, syncView],
  );

  /** Convert a client (screen) coordinate to map units via the SVG CTM. */
  const toMapPoint = useCallback((clientX: number, clientY: number): Point => {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const mapped = pt.matrixTransform(ctm.inverse());
    return { x: mapped.x, y: mapped.y };
  }, []);

  // Boundary pen-tool draft state. Close threshold scales with the map so it
  // feels consistent regardless of zoom (~10px on screen).
  const closeThreshold = 10 / scale;
  const {
    draft: boundaryDraft,
    cursor: boundaryCursor,
    addPoint: addBoundaryPoint,
    finish: finishDraw,
    moveCursor: moveBoundaryCursor,
    undo: undoBoundaryPoint,
    canClose: boundaryCanClose,
  } = useBoundaryDraw({
    active: drawMode,
    closeThreshold,
    geometry: drawGeometry,
    onComplete: (pts) => onBoundaryComplete?.(pts),
  });

  // Shift = ortho lock (axis-aligned segments) while drawing.
  const shiftRef = useRef(false);

  /** Apply ortho lock relative to the last placed vertex when Shift is held. */
  const applyOrtho = useCallback(
    (p: Point): Point => {
      const last = boundaryDraft[boundaryDraft.length - 1];
      if (shiftRef.current && last) return orthoConstrain(last, p);
      return p;
    },
    [boundaryDraft],
  );

  // Keyboard while drawing: Enter finishes, Escape aborts, Backspace undoes the
  // last vertex, Shift toggles ortho lock.
  useEffect(() => {
    if (!drawMode) return;
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack typing in the length box or any other field.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        if (e.key === "Escape") onCancelDraw?.();
        return;
      }
      if (e.key === "Shift") shiftRef.current = true;
      else if (e.key === "Enter") {
        e.preventDefault();
        finishDraw();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onCancelDraw?.();
      } else if (e.key === "Backspace") {
        e.preventDefault();
        undoBoundaryPoint();
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") shiftRef.current = false;
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onUp);
      shiftRef.current = false;
    };
  }, [drawMode, finishDraw, onCancelDraw, undoBoundaryPoint]);

  /** Commit a vertex at an exact distance from the last one, in cursor dir. */
  const commitLength = useCallback(
    (length: number) => {
      const last = boundaryDraft[boundaryDraft.length - 1];
      if (!last || !boundaryCursor || length <= 0) return;
      const aimed = applyOrtho(boundaryCursor);
      const dir = unitVector(last, aimed);
      if (dir.x === 0 && dir.y === 0) return;
      addBoundaryPoint({
        x: last.x + dir.x * length,
        y: last.y + dir.y * length,
      });
    },
    [boundaryDraft, boundaryCursor, applyOrtho, addBoundaryPoint],
  );

  // Persist position/size changes to the backend.
  const updateSectionMutation = useMutation({
    mutationFn: async ({
      sectionId,
      data,
    }: {
      sectionId: string;
      data: Record<string, unknown>;
    }) =>
      (
        await apiClient.put(
          `/api/v1/yards/${yardId}/sections/${sectionId}`,
          data,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["yard-map", yardId] }),
  });

  const handleDragEnd = useCallback(
    (sectionId: string, x: number, y: number) => {
      const s = sections.find((sec) => sec.id === sectionId);
      const after = { x: Math.round(x), y: Math.round(y) };
      if (onCommitGeometry && s) {
        onCommitGeometry(sectionId, { x: s.x, y: s.y }, after);
      } else {
        updateSectionMutation.mutate({ sectionId, data: after });
      }
    },
    [sections, onCommitGeometry, updateSectionMutation],
  );

  const handleResizeEnd = useCallback(
    (
      sectionId: string,
      x: number,
      y: number,
      width: number,
      height: number,
    ) => {
      const s = sections.find((sec) => sec.id === sectionId);
      const after = {
        x: Math.round(x),
        y: Math.round(y),
        width: Math.round(width),
        height: Math.round(height),
      };
      if (onCommitGeometry && s) {
        onCommitGeometry(
          sectionId,
          { x: s.x, y: s.y, width: s.width, height: s.height },
          after,
        );
      } else {
        updateSectionMutation.mutate({ sectionId, data: after });
      }
    },
    [sections, onCommitGeometry, updateSectionMutation],
  );

  // Drag hook
  const {
    isDragging,
    guides,
    boundaries,
    getSectionPosition,
    handlePointerDown: handleDragPointerDown,
    handlePointerMove: handleDragPointerMove,
    handlePointerUp: handleDragPointerUp,
  } = useCanvasDrag({
    sections: sections.map((s) => ({
      id: s.id,
      x: s.x,
      y: s.y,
      width: s.width,
      height: s.height,
    })),
    yardWidth,
    yardHeight,
    onDragEnd: handleDragEnd,
  });

  // Resize hook
  const {
    isResizing,
    getSectionRect,
    handleResizePointerDown,
    handleResizePointerMove,
    handleResizePointerUp,
  } = useCanvasResize({
    yardWidth,
    yardHeight,
    onResizeEnd: handleResizeEnd,
  });

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (drawMode) {
        moveBoundaryCursor(applyOrtho(toMapPoint(e.clientX, e.clientY)));
        return;
      }
      if (isDragging) handleDragPointerMove(e);
      if (isResizing) handleResizePointerMove(e);
    },
    [
      drawMode,
      applyOrtho,
      moveBoundaryCursor,
      toMapPoint,
      isDragging,
      isResizing,
      handleDragPointerMove,
      handleResizePointerMove,
    ],
  );

  const handlePointerUp = useCallback(() => {
    if (isDragging) handleDragPointerUp();
    if (isResizing) handleResizePointerUp();
  }, [isDragging, isResizing, handleDragPointerUp, handleResizePointerUp]);

  // Ruler gutter in yard units
  const rulerGutter = 20 / scale;

  const svgWidth = (yardWidth + rulerGutter) * scale;
  const svgHeight = (yardHeight + rulerGutter) * scale;

  // Live length of the pending segment (last vertex → cursor) for the VCB.
  const liveSegment =
    boundaryDraft.length >= 1 && boundaryCursor
      ? segmentLength(
          boundaryDraft[boundaryDraft.length - 1]!,
          applyOrtho(boundaryCursor),
        )
      : null;

  return (
    <div className="relative overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-secondary)]">
      {/* Scroll/pan viewport */}
      <div
        ref={wrapRef}
        className="overflow-auto"
        style={{
          height: "70vh",
          minHeight: 400,
          cursor: grabbing ? "grabbing" : spaceHeld ? "grab" : undefined,
        }}
        onScroll={syncView}
        onPointerDown={handleWrapPointerDown}
        onPointerMove={handleWrapPointerMove}
        onPointerUp={handleWrapPointerUp}
      >
        <svg
          ref={svgRef}
          width={svgWidth}
          height={svgHeight}
          viewBox={`${-rulerGutter} ${-rulerGutter} ${yardWidth + rulerGutter} ${yardHeight + rulerGutter}`}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onClick={(e) => {
            // In draw mode, every click places (or closes) a vertex.
            if (drawMode) {
              addBoundaryPoint(applyOrtho(toMapPoint(e.clientX, e.clientY)));
              return;
            }
            // Deselect when clicking empty canvas
            if (
              (e.target as SVGElement).tagName === "svg" ||
              (e.target as SVGElement).tagName === "rect" ||
              (e.target as SVGElement).tagName === "polygon" ||
              (e.target as SVGElement).tagName === "polyline"
            ) {
              const target = e.target as SVGElement;
              if (
                !target.closest("[data-section-id]") &&
                !target.closest("[data-feature-id]")
              ) {
                onSelectSection(null);
                onSelectFeature?.(null);
              }
            }
          }}
          onDoubleClick={(e) => {
            // Finish an open polyline draw (fences/gates) on double-click.
            if (drawMode && drawGeometry === "polyline") {
              e.preventDefault();
              finishDraw();
            }
          }}
          onDragOver={(e) => {
            // Allow dropping unplaced storage areas onto the canvas.
            if (onDropArea && e.dataTransfer.types.includes(AREA_DRAG_MIME)) {
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
            }
          }}
          onDrop={(e) => {
            if (!onDropArea) return;
            const areaId = e.dataTransfer.getData(AREA_DRAG_MIME);
            if (!areaId) return;
            e.preventDefault();
            const p = toMapPoint(e.clientX, e.clientY);
            onDropArea(areaId, p.x, p.y);
          }}
          style={{
            minHeight: 400,
            cursor: drawMode ? "crosshair" : undefined,
            pointerEvents: spaceHeld ? "none" : undefined,
          }}
        >
          {/* Grid background */}
          <defs>
            <pattern
              id="grid"
              width={20}
              height={20}
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx={1}
                cy={1}
                r={0.8}
                fill="var(--color-border, #cbd5e1)"
                fillOpacity={0.5}
              />
            </pattern>
          </defs>
          <rect
            x={0}
            y={0}
            width={yardWidth}
            height={yardHeight}
            fill="url(#grid)"
          />

          {/* Facility boundary — custom polygon when defined, else rectangle */}
          {boundaryPoints && boundaryPoints.length >= 3 ? (
            <>
              <polygon
                points={pointsToSvgString(boundaryPoints)}
                fill="none"
                stroke="var(--color-border, #cbd5e1)"
                strokeWidth={1.5 / scale}
                strokeDasharray={`${6 / scale} ${3 / scale}`}
                strokeLinejoin="round"
              />
              <SegmentLengths
                points={boundaryPoints}
                closed
                unit={unit}
                scale={scale}
              />
            </>
          ) : (
            <rect
              x={0}
              y={0}
              width={yardWidth}
              height={yardHeight}
              fill="none"
              stroke="var(--color-border, #cbd5e1)"
              strokeWidth={1.5 / scale}
              strokeDasharray={`${6 / scale} ${3 / scale}`}
            />
          )}

          {/* Setback line — dashed inset from the boundary (keep-clear) */}
          {setbackPoints && setbackPoints.length >= 3 && (
            <polygon
              points={pointsToSvgString(setbackPoints)}
              fill="none"
              stroke="#d97706"
              strokeOpacity={0.7}
              strokeWidth={1 / scale}
              strokeDasharray={`${4 / scale} ${4 / scale}`}
              strokeLinejoin="round"
              style={{ pointerEvents: "none" }}
            />
          )}

          {/* Dimension rulers */}
          <DimensionRulers
            yardWidth={yardWidth}
            yardHeight={yardHeight}
            scale={scale}
            unit={unit}
          />

          {/* Alignment guides and boundary distances (visible during drag) */}
          <AlignmentGuides
            guides={guides}
            boundaries={boundaries}
            yardWidth={yardWidth}
            yardHeight={yardHeight}
            scale={scale}
            visible={isDragging}
          />

          {/* Reference features (buildings, fences, ...) beneath storage areas */}
          <g style={drawMode ? { pointerEvents: "none" } : undefined}>
            {features.map((feature) => (
              <FeatureShape
                key={feature.id}
                id={feature.id}
                label={feature.label}
                kind={feature.kind}
                geometryType={feature.geometry_type}
                points={feature.points as Point[]}
                color={feature.color}
                isSelected={selectedFeatureId === feature.id}
                scale={scale}
                onClick={() => onSelectFeature?.(feature.id)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  onShapeContextMenu?.({
                    type: "feature",
                    id: feature.id,
                    x: e.clientX,
                    y: e.clientY,
                  });
                }}
              />
            ))}
          </g>

          {/* Sections — non-interactive while drawing a boundary */}
          <g style={drawMode ? { pointerEvents: "none" } : undefined}>
            {sections.map((section) => {
              const isSelected = selectedSectionId === section.id;
              const isLocked = lockedIds?.has(section.id) ?? false;
              const shapeType = (section.shape_type ??
                "rectangle") as ShapeType;
              const sectionPoints = section.points as
                | Point[]
                | null
                | undefined;

              // Apply drag offset
              const dragPos = getSectionPosition(
                section.id,
                section.x,
                section.y,
              );
              // Apply resize offset
              const rect = getSectionRect(
                section.id,
                dragPos.x,
                dragPos.y,
                section.width,
                section.height,
              );

              // Scheme badge
              let schemeBadge = null;
              if (section.location_scheme_id && schemes.length > 0) {
                const badgeColor = getSchemeBadgeColor(
                  section.location_scheme_id,
                  schemes,
                );
                schemeBadge = {
                  name: getSchemeName(section.location_scheme_id, schemes),
                  locationCount: section.location_count ?? 0,
                  bg: badgeColor.bg,
                  text: badgeColor.text,
                };
              }

              return (
                <g key={section.id}>
                  <SectionShape
                    id={section.id}
                    x={rect.x}
                    y={rect.y}
                    width={rect.width}
                    height={rect.height}
                    color={section.color}
                    label={section.label}
                    shapeType={shapeType}
                    points={sectionPoints}
                    rotation={section.rotation ?? undefined}
                    assetCount={section.asset_count || 0}
                    capacity={section.capacity}
                    isSelected={isSelected}
                    schemeBadge={schemeBadge}
                    assets={section.assets}
                    scale={scale}
                    locked={isLocked}
                    onClick={() =>
                      onSelectSection(isSelected ? null : section.id)
                    }
                    onPointerDown={(e) => {
                      if (!isLocked) handleDragPointerDown(section.id, e);
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      onShapeContextMenu?.({
                        type: "section",
                        id: section.id,
                        x: e.clientX,
                        y: e.clientY,
                      });
                    }}
                  />

                  {/* Resize handles for selected section (not for custom polygons,
                  whose arbitrary vertices can't be box-resized) */}
                  {isSelected &&
                    !isDragging &&
                    !isLocked &&
                    shapeType !== "polygon" && (
                      <ResizeHandles
                        x={rect.x}
                        y={rect.y}
                        width={rect.width}
                        height={rect.height}
                        scale={scale}
                        onHandlePointerDown={(handle, e) =>
                          handleResizePointerDown(
                            section.id,
                            handle,
                            rect.x,
                            rect.y,
                            rect.width,
                            rect.height,
                            e,
                          )
                        }
                      />
                    )}
                </g>
              );
            })}
          </g>

          {/* Boundary pen-tool draft overlay */}
          {drawMode && boundaryDraft.length > 0 && (
            <g style={{ pointerEvents: "none" }}>
              {/* Placed segments */}
              <polyline
                points={pointsToSvgString(boundaryDraft)}
                fill="none"
                stroke="var(--color-interactive-primary, #7c3aed)"
                strokeWidth={1.5 / scale}
                strokeLinejoin="round"
              />
              {/* Live rubber-band segment to the cursor */}
              {boundaryCursor && (
                <line
                  x1={boundaryDraft[boundaryDraft.length - 1]!.x}
                  y1={boundaryDraft[boundaryDraft.length - 1]!.y}
                  x2={boundaryCursor.x}
                  y2={boundaryCursor.y}
                  stroke="var(--color-interactive-primary, #7c3aed)"
                  strokeWidth={1 / scale}
                  strokeDasharray={`${4 / scale} ${3 / scale}`}
                />
              )}
              {/* Length labels for placed + live segments */}
              <SegmentLengths
                points={
                  boundaryCursor
                    ? [...boundaryDraft, boundaryCursor]
                    : boundaryDraft
                }
                closed={false}
                unit={unit}
                scale={scale}
              />
              {/* Vertices; first is the close target */}
              {boundaryDraft.map((p, i) => (
                <circle
                  key={i}
                  cx={p.x}
                  cy={p.y}
                  r={(i === 0 && boundaryCanClose ? 6 : 3.5) / scale}
                  fill={
                    i === 0
                      ? "var(--color-interactive-primary, #7c3aed)"
                      : "var(--color-surface-secondary, #f8fafc)"
                  }
                  stroke="var(--color-interactive-primary, #7c3aed)"
                  strokeWidth={1.5 / scale}
                />
              ))}
            </g>
          )}

          {/* Empty state */}
          {sections.length === 0 && !drawMode && (
            <text
              x={yardWidth / 2}
              y={yardHeight / 2}
              textAnchor="middle"
              fontSize={14 / scale}
              fill="var(--text-tertiary, #94a3b8)"
            >
              Add sections to build your facility map
            </text>
          )}
        </svg>
      </div>

      {/* Type-a-length value box (VCB) while drawing */}
      {drawMode && boundaryDraft.length >= 1 && (
        <div className="absolute bottom-2 left-2 z-20 flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-raised)] px-2 py-1 shadow-[var(--shadow-md)]">
          <span className="text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)]">
            Length
          </span>
          <input
            type="number"
            min={0}
            value={lengthInput}
            onChange={(e) => setLengthInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && lengthInput !== "") {
                commitLength(Number(lengthInput));
                setLengthInput("");
              }
            }}
            placeholder={
              liveSegment != null ? String(Math.round(liveSegment)) : ""
            }
            className="h-6 w-16 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-1.5 text-xs text-[var(--text-primary)]"
          />
          <span className="text-[10px] text-[var(--text-tertiary)]">
            {unit} · Shift=straight
          </span>
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute bottom-2 right-2 z-20 flex items-center gap-0.5 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-raised)]/95 px-1 py-0.5 shadow-[var(--shadow-md)] backdrop-blur">
        <button
          type="button"
          title="Zoom out"
          onClick={() => zoomBy(1 / 1.2)}
          className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title="Reset zoom (fit)"
          onClick={resetZoom}
          className="min-w-[2.75rem] rounded-[var(--radius-sm)] px-1 text-center text-[11px] font-medium tabular-nums text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          title="Zoom in"
          onClick={() => zoomBy(1.2)}
          className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <div className="mx-0.5 h-4 w-px bg-[var(--border-default)]" />
        <button
          type="button"
          title="Fit to view"
          onClick={resetZoom}
          className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title="Zoom to selection"
          onClick={zoomToSelection}
          disabled={!hasSelection}
          className="flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] disabled:opacity-40"
        >
          <LocateFixed className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title={showMinimap ? "Hide minimap" : "Show minimap"}
          onClick={() => setShowMinimap((v) => !v)}
          className={`flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)] hover:bg-[var(--surface-sunken)] ${showMinimap ? "text-[var(--color-interactive-primary)]" : "text-[var(--text-tertiary)]"}`}
        >
          <Map className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Minimap */}
      {showMinimap && (
        <MapMinimap
          yardWidth={yardWidth}
          yardHeight={yardHeight}
          boundaryPoints={boundaryPoints ?? null}
          sections={sections.map((s) => ({
            id: s.id,
            x: s.x,
            y: s.y,
            width: s.width,
            height: s.height,
            color: s.color,
          }))}
          features={features}
          scale={scale}
          view={view}
          onNavigate={centerOn}
        />
      )}
    </div>
  );
}

export default YardCanvas;
